import { z } from "zod"

import {
	Alert,
	Button,
	Form,
	FormSelect,
	FormSwitch,
	Loader,
	SubmitButton,
	useZodForm,
} from "@productlane/ui"

import { toast } from "@/components/toast"
import { env } from "@/env"
import { api } from "@/lib/api"
import { SettingsContainer } from "@/screens/settings/settings-switches/settings-container"
import { SettingsHeader } from "../../settings-header"
import { ImportPortalBadge, useImportPortalId } from "../import-portal"
import { runHeadline, runIsWorking, WorkingDots } from "./run-status"

const StatusChoice = z.enum(["new", "done", "close-loop"])

const ProductlaneStatusOptions = [
	{ label: "New", value: "new" },
	{ label: "Done", value: "done" },
	{ label: "Close loop", value: "close-loop" },
]

const DateWindowOptions = [
	{ label: "Everything", value: "all" },
	{ label: "Last 30 days", value: "30" },
	{ label: "Last 90 days", value: "90" },
	{ label: "Last 12 months", value: "365" },
]

const FormSchema = z.object({
	dateWindow: z.string(),
	openStatus: StatusChoice,
	snoozedStatus: StatusChoice,
	closedStatus: StatusChoice,
	importHelpCenter: z.boolean(),
	importAttachments: z.boolean(),
})

function Count({ label, value }: { label: string; value: number | null }) {
	return (
		<div className="flex items-baseline justify-between py-1.5 text-sm">
			<span className="text-secondary">{label}</span>
			<span className="font-medium tabular-nums text-primary">
				{value === null ? "Not available" : value.toLocaleString()}
			</span>
		</div>
	)
}

function Progress({ runId }: { runId: string }) {
	// The run writes its counts as it goes, so polling is what makes them move.
	const runQuery = api.intercomImport.latestRun.useQuery(undefined, {
		refetchInterval: 3000,
	})
	const run = runQuery.data
	if (!run || run.id !== runId) return null

	return (
		<SettingsContainer>
			<div className="p-4">
				<div className="mb-2 flex items-center text-sm font-medium">
					{runHeadline(run.status)}
					{runIsWorking(run.status) && <WorkingDots />}
				</div>
				<Count label="Conversations" value={run.conversationsImported} />
				<Count label="Articles" value={run.articlesImported} />
				{run.failureReason && (
					<p className="mt-2 text-sm text-secondary">{run.failureReason}</p>
				)}
			</div>
		</SettingsContainer>
	)
}

export function IntercomImportPage() {
	const portalInstanceId = useImportPortalId()
	const preflightQuery = api.intercomImport.preflight.useQuery(undefined, {
		staleTime: 60_000,
		retry: false,
	})
	const runQuery = api.intercomImport.latestRun.useQuery(undefined)
	const startImport = api.intercomImport.startImport.useMutation({
		onSuccess: async () => {
			toast.success({
				title: "Import started",
				description: "You can leave this page, it keeps running.",
			})
			await runQuery.refetch()
		},
		onError: (error) => {
			toast.error({
				title: "Could not start the import",
				description: error.message,
			})
		},
	})

	const form = useZodForm({
		schema: FormSchema,
		defaultValues: {
			dateWindow: "all",
			openStatus: "new",
			snoozedStatus: "done",
			closedStatus: "done",
			importHelpCenter: true,
			importAttachments: true,
		},
	})

	const running =
		runQuery.data?.status === "PENDING" || runQuery.data?.status === "RUNNING"

	return (
		<>
			<SettingsHeader
				title="Import from Intercom"
				subtitle="Bring your conversations, contacts and help center across. Nothing is written until you start the import, and your customers hear nothing."
				titleSuffix={<ImportPortalBadge />}
			/>

			{preflightQuery.isLoading && (
				<div className="flex justify-center py-8">
					<Loader />
				</div>
			)}

			{preflightQuery.isError && (
				<Alert
					header="Reconnect Intercom to migrate"
					variant="warning"
					className="mb-6"
				>
					<div>
						Intercom refused the request. Your connection was granted before we
						read conversations and articles, so reconnect to widen it, then come
						back here.
					</div>
					<Button
						className="mt-2"
						onClick={() => {
							window.location.href = `${env.VITE_API_URL}/api/intercom/connect`
						}}
					>
						Reconnect Intercom
					</Button>
				</Alert>
			)}

			{preflightQuery.data && (
				<SettingsContainer>
					<div className="p-4">
						<div className="mb-2 text-sm font-medium">What we found</div>
						<Count
							label="Conversations"
							value={preflightQuery.data.conversations}
						/>
						<Count label="Contacts" value={preflightQuery.data.contacts} />
						<Count label="Companies" value={preflightQuery.data.companies} />
						<Count
							label="Help center articles"
							value={preflightQuery.data.articles}
						/>
						<Count
							label="Fin content sources"
							value={preflightQuery.data.finContentSources}
						/>
					</div>
				</SettingsContainer>
			)}

			{runQuery.data && <Progress runId={runQuery.data.id} />}

			<Form
				form={form}
				className="mt-6 flex flex-col gap-4"
				onSubmit={async ({ dateWindow, ...values }) => {
					await startImport.mutateAsync({
						...values,
						portalInstanceId,
						...(dateWindow === "all" ? {} : { daysBack: Number(dateWindow) }),
					})
				}}
			>
				<div className="text-sm font-medium">How far back to go</div>
				<div className="flex items-center gap-2">
					<div className="flex-1 text-sm text-secondary">
						Conversations updated within
					</div>
					<FormSelect name="dateWindow" options={DateWindowOptions} />
				</div>

				<div className="text-sm font-medium">Where each state goes</div>
				<div className="flex items-center gap-2">
					<div className="flex-1 text-sm text-secondary">Open</div>
					<FormSelect name="openStatus" options={ProductlaneStatusOptions} />
				</div>
				<div className="flex items-center gap-2">
					<div className="flex-1 text-sm text-secondary">Snoozed</div>
					<FormSelect name="snoozedStatus" options={ProductlaneStatusOptions} />
				</div>
				<div className="flex items-center gap-2">
					<div className="flex-1 text-sm text-secondary">Closed</div>
					<FormSelect name="closedStatus" options={ProductlaneStatusOptions} />
				</div>

				<FormSwitch
					name="importHelpCenter"
					label="Import the help center"
					description="Collections become groups, articles keep their draft state."
				/>
				<FormSwitch
					name="importAttachments"
					label="Import attachments"
					description="Copies files from Intercom into your own storage."
				/>

				<div className="flex justify-end">
					<SubmitButton
						isLoading={startImport.isLoading || running}
						disabled={running}
					>
						{running ? "Import running" : "Start import"}
					</SubmitButton>
				</div>
			</Form>
		</>
	)
}
